import React, { FC, useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { format } from 'date-fns';

import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';

import LoadingWrapper from '../../layout/LoadingWrapper';

import IssueReport from '../model';

import axios from '../../utils/axios.utils';

const IssueReportDetailPage: React.FC = () => {
  let history = useHistory();

  let { issueReportId } = useParams<{ issueReportId: string }>();

  let [issueReport, setIssueReport] = useState<IssueReport>();
  let [loading, setLoading] = useState<boolean>(false);
  let [errorMessage, setErrorMessage] = useState<string>('');
  let [reportable, setReportable] = useState<Record<string, string>>({});

  const fetchIssueReport = async () => {
    setLoading(true);
    try {
      const response: any = await axios.get(`/issue_reports/${issueReportId}`);
      setIssueReport(response.data.result);
      setReportable(response.data.reportable);
    } catch (error) {
      setErrorMessage('Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchIssueReport();
  }, [issueReportId]);

  const formatValue = (value: any): string => {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    return String(value);
  };

  const formatLabel = (key: string): string => {
    return key
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const formatDate = (dateString: string | undefined): string => {
    if (!dateString) return '-';
    try {
      return format(new Date(dateString), 'MM/dd/yyyy hh:mm a');
    } catch {
      return dateString;
    }
  };

  return (
    <LoadingWrapper isLoading={loading} message="Loading">
      <Grid container spacing={3}>
        {errorMessage && (
          <Grid item xs={12}>
            <Typography color="error">{errorMessage}</Typography>
          </Grid>
        )}

        <Grid item xs={12}>
          <Card sx={{ p: 3 }}>
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Issue Report Details
              </Typography>
            </Box>

            <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>ID:</strong> {issueReport?.id || '-'}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Status:</strong> {formatValue(issueReport?.status)}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Submitted:</strong> {formatDate(issueReport?.updated_at_with_time)}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>User Name:</strong> {issueReport?.user_name || '-'}
              </Typography>
              {issueReport?.message && (
                <>
                  <Typography variant="body2" sx={{ mb: 1, mt: 2 }}>
                    <strong>Message:</strong>
                  </Typography>
                  <Typography variant="body2" sx={{ pl: 2, fontStyle: 'italic' }}>
                    {issueReport.message}
                  </Typography>
                </>
              )}
              <Typography variant="body2" sx={{ mb: 1, mt: 2 }}>
                <strong>Reportable ID:</strong> {issueReport?.reportable_id || '-'}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Company Name:</strong> {formatValue(issueReport?.company_name)}
              </Typography>
            </Box>

            <Box sx={{ mt: 2 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => history.push(`/${issueReport?.class_param}s/${issueReport?.reportable_id}`)}
              >
                View {issueReport?.class_title} Page
              </Button>
            </Box>
          </Card>
        </Grid>

        {Object.keys(reportable).length > 0 && (
          <Grid item xs={12}>
            <Card sx={{ p: 3 }}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="h6" gutterBottom>
                  {issueReport?.class_title} Item Page
                </Typography>
              </Box>

              <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Grid container spacing={2}>
                  {Object.entries(reportable).map(([key, value]) => {
                    const isLongValue = String(value).length > 80;
                    return (
                      <Grid item xs={12} md={isLongValue ? 12 : 6} key={key}>
                        <Typography variant="body2">
                          <strong>{formatLabel(key)}:</strong> {formatValue(value)}
                        </Typography>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            </Card>
          </Grid>
        )}
      </Grid>
    </LoadingWrapper>
  );
};

export default IssueReportDetailPage;
